import { inject, Injectable } from "@angular/core";
import { buffer, debounceTime, filter, fromEvent, map, tap } from "rxjs";
import { S25Util } from "../util/s25-util";
import { IdCardsStore } from "../modules/student-engagement/id-cards/id.cards.store";

@Injectable()
export class CardReaderService {
    // 10 ms wasn't always enough, hence 20 ms
    private static readonly DEBOUNCE_TIME = 20;

    private static readonly FORMAT =
        /^(%(?<track1>[A-Z0-9 !"#%&'()*+,\-./:;<=>^]*)\?)?(;(?<track2>[0-9:;<=>]*)\?)?([+;](?<track3>[0-9:;<=>]*)\?)?$/;

    private readonly store = inject(IdCardsStore);

    private readonly keydown$ = fromEvent<KeyboardEvent>(window, "keydown");
    private lastKeyPress = Date.now();
    readonly swipe$ = this.keydown$.pipe(
        tap((event) => {
            const now = Date.now();
            // Prevent default on space events. Distinguish card reader events from keyboard events by their speed
            if (now - this.lastKeyPress <= CardReaderService.DEBOUNCE_TIME) {
                // "/" is for quick search
                const preventKeys = [" ", "/", "Enter"];
                if (preventKeys.includes(event.key)) event.preventDefault();
            }
            this.lastKeyPress = now;
        }),
        filter((event) => !/shift|meta/i.test(event.key)),
        buffer(this.keydown$.pipe(debounceTime(CardReaderService.DEBOUNCE_TIME))),
        filter((events) => events[events.length - 1]?.key === "Enter" || events.length > 12),
        map((events) =>
            events
                .filter((e) => e.key !== "Enter")
                .map((event) => event.key)
                .join(""),
        ),
        filter(CardReaderService.looksLikeCard),
        map(CardReaderService.parse),
    );

    static looksLikeCard(card: string): boolean {
        if (!card) return false;
        const match = card.match(CardReaderService.FORMAT);
        // Make sure that at least one track exists
        return !!match && Object.values(match.groups ?? {}).some((value) => value);
    }

    static parse(card: string): CardSwipe {
        if (!card) return { tracks: [], error: true };
        const tracks: CardSwipe["tracks"] = [];
        const match = card.match(CardReaderService.FORMAT)?.groups;
        if (!!match) {
            let i = 1;
            while ("track" + i in match) {
                const value = match["track" + i] ?? "";
                tracks.push({
                    value,
                    error: value === "E", // MagTek 21040145 uses E for error
                });
                i++;
            }
        }
        return { tracks, error: tracks.some((track) => track.error) };
    }

    getFields(card: CardSwipe): Map<string, string[]> {
        const configs = this.store.regexes();
        const regexes = configs.map((config) => config.map((regex) => new RegExp(regex, "i")));

        const fields = new Map<string, string[]>();
        outer: for (const regex of regexes) {
            const fieldEntries: [string, string][] = [];
            for (const [t, track] of S25Util.array.enumerate(card.tracks)) {
                const match = track.value.match(regex[t]);
                if (!match) continue outer;
                fieldEntries.push(...Object.entries(match.groups ?? {}));
            }

            for (const [field, value] of fieldEntries) {
                if (!fields.has(field)) fields.set(field, []);
                fields.get(field)!.push(value);
            }
        }

        return fields;
    }

    byField$(field: string) {
        return this.swipe$.pipe(
            filter(() => this.store.hasConfigs()),
            map((card) => this.getFields(card).get(field)),
            filter(Boolean),
        );
    }
}

export type CardSwipe = { tracks: { value: string; error: boolean }[]; error: boolean };
